// Auth & User Types
export interface Usuario {
  _id: string;
  nombre: string;
  email: string;
  role: 'ADMIN' | 'VENDEDOR' | 'ASISTENTE';
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AuthUser {
  _id: string;
  email: string;
  nombre: string;
  role: 'ADMIN' | 'VENDEDOR' | 'ASISTENTE';
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export interface AdminUpdatePasswordRequest {
  newPassword: string;
}

// Core Business Types
export interface Lotificacion {
  _id: string;
  nombre: string;
  direccion: string;
  propietario: string;
  telefono?: string;
  reservaMonto: number;
  reservaDias: number;
  comisionPct: number;
  tasaInteres: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface LoteCounts {
  disponibles: number;
  reservados: number;
  vendidos: number;
  cancelados: number;
  total: number;
}

export interface LotificacionWithKPIs extends Lotificacion {
  loteCounts: LoteCounts;
}

// Dashboard API response for lotificaciones (different structure)
export interface DashboardLotificacion {
  lotificacionId: string;
  nombre: string;
  disponibles: number;
  reservados: number;
  vendidos: number;
}

// Extended indicators for dashboard per lotificación
export interface DashboardLotificacionIndicadores extends DashboardLotificacion {
  reservasActivas: number;
  reservasPorVencer: number;
  reservasVencidas: number;
  ingresosMes: number;
  ventasMes: number;
  montoVentasMes: number;
  comisionMes: number;
}

export interface LotificacionDetail extends Lotificacion {
  poligonos: Poligono[];
}

export interface Poligono {
  _id: string;
  lotificacionId: string;
  nombre: string;
  createdAt: string;
  updatedAt: string;
}

export interface Comprador {
  _id: string;
  tipoDoc: 'DUI' | 'PASAPORTE';
  numeroDoc: string;
  nombre: string;
  telefono: string;
  direccion: string;
  createdAt: string;
  updatedAt: string;
}

export interface Comprobante {
  _id: string;
  monto: number;
  path: string;
  uploadedAt: string;
  uploadedBy: string; // Usuario ObjectId (may be populated in detalle)
}

export interface ComprobantePagoPrima {
  _id: string;
  monto: number;
  path: string;
  uploadedAt: string;
  uploadedBy: string; // Usuario ObjectId
}

export interface Reserva {
  _id: string;
  vendedorId: string;
  compradorId: string;
  montoTotal: number;
  comprobantes: Comprobante[];
  createdAt: string;
  expiresAt: string;
  status: 'ACTIVA' | 'VENCIDA' | 'CANCELADA';
}

export type TipoDocAdj = 
  | 'COMPROBANTE_RESERVA' 
  | 'DUI_PASAPORTE' 
  | 'IMPUESTOS_USA' 
  | 'PODER_LEGAL' 
  | 'RECIBO_SERVICIO' 
  | 'PROMESA_VENTA_FIRMADA';

export interface Documento {
  tipo: TipoDocAdj;
  path: string;
  uploadedAt: string;
}

export interface HistoryEntry {
  fecha: string;
  usuarioId: string;
  usuarioNombre?: string;
  de: EstadoLote;
  a: EstadoLote;
}

export interface Lote {
  _id: string;
  lotificacionId: string;
  poligonoId: string;
  numero: string;
  areaM2: number;
  areaV2: number;
  precioVara: number;
  precioContado: number;
  primaPct: number;
  primaValor: number;
  financiamiento: number;
  cuotas: {
    '12m': number;
    '36m': number;
    '60m': number;
    '96m': number;
    '120m': number;
  };
  estado: EstadoLote;
  compradorId?: string;
  reservas: Reserva[];
  comprobantesPagoPrima: ComprobantePagoPrima[];
  montoPrimaPagado: number;
  documentos: Documento[];
  history: HistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

// Populated Lote response from GET /lotes/:id
export interface LoteDetail extends Omit<Lote, 'lotificacionId' | 'poligonoId' | 'compradorId' | 'reservas'> {
  lotificacionId: {
    _id: string;
    nombre: string;
    direccion: string;
  };
  poligonoId: {
    _id: string;
    nombre: string;
  };
  compradorId?: {
    _id: string;
    nombre: string;
    tipoDoc: TipoDocumento;
    numeroDoc: string;
    telefono: string;
  } | null;
  reservas: ReservaSubdoc[];
  planPagos?: PlanPagos;
}

// Reserva subdocument with populated fields  
export interface ReservaSubdoc {
  _id: string;
  vendedorId: {
    _id: string;
    nombre: string;
    email: string;
  };
  compradorId: {
    _id: string;
    nombre: string;
    tipoDoc: TipoDocumento;
    numeroDoc: string;
  };
  montoTotal: number;
  comprobantes: Array<Comprobante & { uploadedBy: { _id: string; nombre: string; email: string } | string }>;
  createdAt: string;
  expiresAt: string;
  status: StatusReserva;
}

// Populated list item shape from GET /lotes
export interface LoteListItem extends Omit<Lote, 'lotificacionId' | 'poligonoId' | 'compradorId'> {
  lotificacionId: { _id: string; nombre: string };
  poligonoId: { _id: string; nombre: string };
  compradorId?: { _id: string; nombre: string; tipoDoc?: TipoDocumento; numeroDoc?: string; telefono?: string } | null;
}

// Enums
export type EstadoLote = 'DISPONIBLE' | 'RESERVADO' | 'VENDIDO' | 'CANCELADO';
export type StatusReserva = 'ACTIVA' | 'VENCIDA' | 'CANCELADA';
export type TipoDocumento = 'DUI' | 'PASAPORTE';

// API Response Types
export interface ApiResponse<T> {
  data: T;
}

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// Shape used by endpoints that return `{ docs, totalDocs }`
export interface PaginatedDocsResponse<T> {
  docs: T[];
  page: number;
  limit: number;
  totalDocs: number;
  totalPages: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  nextPage?: number | null;
  prevPage?: number | null;
  pagingCounter?: number;
  meta?: Record<string, unknown>;
}

export interface ApiError {
  statusCode: number;
  message: string | string[];
  error: string;
  details?: Array<{
    field: string;
    message: string;
  }>;
}

// Dashboard Types
export interface DashboardStats {
  totalLotes: number;
  lotesDisponibles: number;
  lotesReservados: number;
  lotesVendidos: number;
  reservasVencidas: number;
  reservasPorVencer: number;
  ventasDelMes: number;
  ingresosTotales: number;
}

// Form Types
export interface CreateUsuarioForm {
  nombre: string;
  email: string;
  password: string;
  role: 'ADMIN' | 'VENDEDOR' | 'ASISTENTE';
  activo: boolean;
}

export interface UpdateUsuarioForm {
  nombre?: string;
  role?: 'ADMIN' | 'VENDEDOR' | 'ASISTENTE';
  activo?: boolean;
}

export interface CreateLotificacionForm {
  nombre: string;
  direccion: string;
  propietario: string;
  telefono?: string;
  reservaMonto: number;
  reservaDias: number;
  comisionPct: number;
  tasaInteres: number;
  createdBy: string;
}

export interface UpdateLotificacionForm {
  nombre?: string;
  direccion?: string;
  propietario?: string;
  telefono?: string;
  reservaMonto?: number;
  reservaDias?: number;
  comisionPct?: number;
  tasaInteres?: number;
}

export interface CreatePoligonoForm {
  nombre: string;
}

export interface ImportLotesResponse {
  inserted: number;
  updated: number;
  errors: Array<{
    row: number;
    msg: string;
  }>;
}

// Reserva forms
export interface CreateReservaForm {
  compradorId?: string;
  compradorData?: {
    tipoDoc: TipoDocumento;
    numeroDoc: string;
    nombre: string;
    telefono: string;
    direccion: string;
  };
  montos: number[];
  comprobantes: File[];
}

export interface CreateReservaResponse {
  message: string;
  reserva: {
    _id: string;
    vendedorId: string;
    compradorId: string;
    montoTotal: number;
    comprobantes: Comprobante[];
    createdAt: string;
    expiresAt: string;
    status: StatusReserva;
  };
  loteEstado: EstadoLote;
}

// Venta forms
export interface VenderLoteForm {
  compradorId: string;
  extranjero?: boolean;
  apoderado?: boolean;
  // Payment plan fields
  fechaInicio: string; // ISO date string
  numeroPagos: number; // 12, 36, 60, 96, or 120 months
  tasaInteres?: number; // Annual interest rate percentage (optional, uses lotificación default if not provided)
  documentos: {
    DUI_PASAPORTE: File;
    RECIBO_SERVICIO: File;
    PROMESA_VENTA_FIRMADA: File;
    IMPUESTOS_USA?: File;
    PODER_LEGAL?: File;
    // Note: COMPROBANTE_PAGO_PRIMA no longer required, validated automatically via montoPrimaPagado >= primaValor
  };
}

// Upload responses
export interface UploadResponse {
  path: string;
}

// Update Lote form
export interface UpdateLoteForm {
  areaM2?: number;
  areaV2?: number;
  precioVara?: number;
  precioContado?: number;
  primaPct?: number;
  primaValor?: number;
  cuotas?: {
    '12m': number;
    '36m': number;
    '60m': number;
    '96m': number;
    '120m': number;
  };
}

// Update Comprador form (all fields optional per API spec)
export interface UpdateCompradorForm {
  tipoDoc?: TipoDocumento;
  numeroDoc?: string;
  nombre?: string;
  telefono?: string;
  direccion?: string;
}

// Payment Types
export type EstadoPagoMensualidad = 'PENDIENTE' | 'PAGADO' | 'VENCIDO' | 'PARCIAL';

export interface PlanPagos {
  fechaInicio: string;
  numeroPagos: number;
  tasaInteres: number;
  montoPrestamo: number;
  mensualidad: number;
  fechaEstimadaPago: string;
}

export interface PagoMensualidadRequest {
  numeroCuota: number;
  montoPagado: number;
  fechaPago: string; // YYYY-MM-DD format
  notas?: string;
  comprobante: File;
}

export interface PagoMensualidadResponse {
  _id: string;
  numeroCuota: number;
  montoPagado: number;
  montoEsperado: number;
  fechaPago: string;
  fechaVencimiento: string;
  estado: EstadoPagoMensualidad;
  comprobantePath: string;
  registradoPor: string;
  fechaRegistro: string;
  notas?: string;
}

export interface CuotaCalculada {
  numeroCuota: number;
  montoMensualidad: number;
  fechaVencimiento: string;
  estado: EstadoPagoMensualidad;
  montoPagado: number;
  diferencia: number;
  diasAtraso: number;
}

export interface ResumenPagos {
  totalCuotas: number;
  cuotasPagadas: number;
  cuotasPendientes: number;
  cuotasVencidas: number;
  montoPagadoTotal: number;
  montoEsperadoTotal: number;
  montoPendiente: number;
  porcentajePagado: number;
}

export interface ProximaCuota {
  numeroCuota: number;
  fechaVencimiento: string;
  montoEsperado: number;
  diasParaVencer: number;
}

export interface ResumenPagoMensualidades {
  loteId: string;
  planPagos: PlanPagos;
  resumen: ResumenPagos;
  cuotas: CuotaCalculada[];
  historialPagos: PagoMensualidadResponse[];
  proximaCuota?: ProximaCuota;
}

// Query Keys
export const QUERY_KEYS = {
  USUARIOS: 'usuarios',
  USUARIO: 'usuario',
  DASHBOARD: 'dashboard',
  LOTIFICACIONES: 'lotificaciones',
  LOTIFICACION: 'lotificacion',
  LOTES: 'lotes',
  LOTE: 'lote',
  COMPRADORES: 'compradores',
  COMPRADOR: 'comprador',
  POLIGONOS: 'poligonos',
  RESERVAS_VENCIDAS: 'reservas-vencidas',
  RESERVAS_POR_VENCER: 'reservas-por-vencer',
  REPORTES_RESERVAS: 'reportes-reservas',
  REPORTES_VENTAS: 'reportes-ventas',
} as const;

// Reportes
export interface ReporteReservasItem {
  loteId: string;
  monto: number;
  lotificacionNombre: string;
  poligonoNombre: string;
  numeroLote: string;
  compradorNombre: string;
}

export interface ReporteReservasResponse {
  reservas: ReporteReservasItem[];
  totalCaptado: number;
}

export interface ReporteVentasItem {
  loteId: string;
  montoVenta: number;
  comision: number;
  lotificacionNombre: string;
  poligonoNombre: string | null;
  numeroLote: string;
  compradorNombre: string | null;
}

export interface ReporteVentasResponse {
  ventas: ReporteVentasItem[];
  totalVentas: number;
  totalComision: number;
}